import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ClickToCall } from '@/components/ClickToCall';
import { Linkedin } from 'lucide-react';

// Pipeline stages with top color bars (matching Salesflare style)
const PIPELINE_STAGES = [
  { id: 'new', label: 'Lead', color: '#EF4444', bgColor: 'bg-red-500' },
  { id: 'contacted', label: 'Contacted', color: '#F59E0B', bgColor: 'bg-amber-500' },
  { id: 'meeting', label: 'Meeting Set', color: '#84CC16', bgColor: 'bg-lime-500' },
  { id: 'qualified', label: 'Qualified', color: '#22C55E', bgColor: 'bg-green-500' },
  { id: 'proposal', label: 'Proposal made', color: '#10B981', bgColor: 'bg-emerald-500' },
  { id: 'closed_won', label: 'Won', color: '#0D9488', bgColor: 'bg-teal-600' },
  { id: 'closed_lost', label: 'Lost', color: '#6B7280', bgColor: 'bg-gray-500' },
];

// Industry colors for chips
const INDUSTRY_COLORS = {
  'Technology': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  'Healthcare': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  'Finance': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  'Education': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  'Retail': { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200' },
  'Manufacturing': { bg: 'bg-slate-100', text: 'text-slate-700 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-700' },
  'Real Estate': { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200' },
  'E-commerce': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  'default': { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
};

// Priority indicators
const PRIORITY_COLORS = {
  'high': 'bg-red-400',
  'medium': 'bg-amber-400',
  'low': 'bg-green-400',
};

// Calculate priority based on deal value and probability
const calculatePriority = (deal) => {
  const value = deal.deal_value || 0;
  const prob = deal.win_probability || 50;
  const score = (value / 100000) * (prob / 100);
  if (score > 5) return 'high';
  if (score > 2) return 'medium';
  return 'low';
};

// Info icon component
const InfoIcon = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Format currency in INR
const formatCurrency = (value) => {
  if (!value) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

// Clean KPI Card Component - matching dashboard exactly
const KPICard = ({ title, value, trend, subValue }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {title}
        <InfoIcon />
      </span>
      <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-bold text-slate-900 dark:text-white">{value}</span>
      {trend !== undefined && (
        <span className="inline-flex items-center text-xs font-medium text-green-600">
          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          {trend}
        </span>
      )}
    </div>
    {subValue && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subValue}</p>}
  </div>
);

// Deal Card Component - Clean white design
function DealCard({ deal, isDragging, onClick }) {
  const priority = calculatePriority(deal);
  const industryColor = INDUSTRY_COLORS[deal.industry] || INDUSTRY_COLORS.default;
  
  return (
    <div
      onClick={() => onClick(deal)}
      className={`
        bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 cursor-pointer
        transition-all duration-200 group
        ${isDragging 
          ? 'shadow-xl scale-105 rotate-2 border-blue-400' 
          : 'shadow-sm hover:shadow-md hover:border-slate-300 dark:border-slate-600'
        }
      `}
      data-testid={`deal-card-${deal.id}`}
    >
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[priority]}`} title={`${priority} priority`} />
          <span className="text-[10px] text-slate-400 uppercase">{priority}</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Click-to-Call Icon */}
          {deal.phone && deal.phone !== '-' && (
            <div onClick={(e) => e.stopPropagation()}>
              <ClickToCall 
                phone={deal.phone} 
                leadId={deal.id} 
                leadName={deal.name}
                variant="icon"
                size="sm"
              />
            </div>
          )}
          {/* LinkedIn Icon */}
          {deal.linkedin_website && (
            <a
              href={deal.linkedin_website.startsWith('http') ? deal.linkedin_website : `https://www.linkedin.com/in/${deal.linkedin_website}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-0.5 text-[#0A66C2] hover:bg-[#0A66C2]/10 rounded transition-colors"
              title="View LinkedIn"
            >
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
      
      {/* Company Name */}
      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate mb-1">
        {deal.organization || 'No Company'}
      </p>
      
      {/* Contact Name */}
      <h4 className="font-semibold text-slate-900 dark:text-white text-sm leading-tight mb-2 line-clamp-1 group-hover:text-blue-600 transition-colors">
        {deal.name}
      </h4>
      
      {/* Middle Row - Industry & Probability */}
      <div className="flex items-center justify-between mb-2">
        {deal.industry ? (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${industryColor.bg} ${industryColor.text} truncate max-w-[80px]`}>
            {deal.industry}
          </span>
        ) : <span />}
        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-slate-100 text-slate-600 dark:text-slate-400 rounded">
          {deal.win_probability || 50}% prob
        </span>
      </div>
      
      {/* Deal Value */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
        <span className="text-sm font-semibold text-emerald-600">
          {formatCurrency(deal.deal_value)}
        </span>
        {deal.expected_close_date && (
          <span className="text-[10px] text-slate-400">
            {new Date(deal.expected_close_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
      
      {/* Owner */}
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
        <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 dark:text-slate-400 text-[10px] font-semibold">
          {deal.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate flex-1">
          {deal.owner_name || 'Unassigned'}
        </span>
      </div>
    </div>
  );
}

// Sortable Wrapper for Deal Card
function SortableDealCard({ deal, onClick }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="touch-none cursor-grab active:cursor-grabbing"
    >
      <DealCard deal={deal} isDragging={isDragging} onClick={onClick} />
    </div>
  );
}

// Pipeline Column Component - White background with colored top bar only
function PipelineColumn({ stage, deals, onDealClick, isOver }) {
  const { setNodeRef } = useDroppable({ id: stage.id });
  const dealIds = useMemo(() => deals.map(d => d.id), [deals]);
  const totalValue = deals.reduce((sum, d) => sum + (d.deal_value || 0), 0);

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 flex flex-col bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 transition-all duration-200 ${isOver ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
      data-testid={`column-${stage.id}`}
    >
      {/* Colored top bar - thin line only */}
      <div className={`h-1.5 ${stage.bgColor}`} />
      
      {/* Column Header - White background */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">{stage.label}</h3>
            <span className="text-xs text-slate-400 font-medium">
              {deals.length}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">{formatCurrency(totalValue)}</p>
      </div>

      {/* Cards Container - White background */}
      <div className="flex-1 p-2 overflow-y-auto min-h-[300px] max-h-[calc(100vh-380px)] space-y-2 bg-white dark:bg-slate-800">
        <SortableContext items={dealIds} strategy={verticalListSortingStrategy}>
          {deals.map(deal => (
            <SortableDealCard key={deal.id} deal={deal} onClick={onDealClick} />
          ))}
        </SortableContext>
        {deals.length === 0 && (
          <div className={`h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors duration-200 ${isOver ? 'border-blue-400 bg-blue-50' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900'}`}>
            <svg className="w-8 h-8 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-xs text-slate-400">Drop deals here</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Deal Detail Drawer - Clean styling
function DealDrawer({ deal, isOpen, onClose, onUpdate, stages }) {
  const [editedDeal, setEditedDeal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (deal) setEditedDeal({ ...deal });
  }, [deal]);

  const handleSave = async () => {
    if (!editedDeal) return;
    setSaving(true);
    try {
      await onUpdate(editedDeal);
      onClose();
    } catch (error) {
      console.error('Error updating deal:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !deal || !editedDeal) return null;

  const industryColor = INDUSTRY_COLORS[deal.industry] || INDUSTRY_COLORS.default;
  const currentStage = stages.find(s => s.id === editedDeal.status) || stages.find(s => s.id === deal.status);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <div 
              className="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded"
              style={{ backgroundColor: `${currentStage?.color}15`, color: currentStage?.color }}
            >
              {currentStage?.label || deal.status}
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">{deal.organization || 'No Company'}</p>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-1">{deal.name}</h2>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase">Deal Value</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">{formatCurrency(deal.deal_value)}</p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase">Win Probability</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xl font-bold text-slate-900 dark:text-white">{editedDeal.win_probability || 50}%</p>
              </div>
            </div>
          </div>

          {/* Probability Slider */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
            <label className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase block mb-3">Adjust Probability</label>
            <input
              type="range"
              min="0"
              max="100"
              value={editedDeal.win_probability || 50}
              onChange={(e) => setEditedDeal({ ...editedDeal, win_probability: parseInt(e.target.value) })}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Contact Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Email</label>
                <p className="text-sm text-slate-700 dark:text-slate-300">{deal.email || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Phone</label>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-700 dark:text-slate-300">{deal.phone || '-'}</p>
                  {deal.phone && deal.phone !== '-' && (
                    <ClickToCall 
                      phone={deal.phone} 
                      leadId={deal.id} 
                      leadName={deal.name}
                      variant="icon"
                      size="sm"
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Industry</label>
                {deal.industry ? (
                  <span className={`text-xs font-medium px-2 py-1 rounded ${industryColor.bg} ${industryColor.text}`}>
                    {deal.industry}
                  </span>
                ) : <span className="text-sm text-slate-400">Not set</span>}
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Expected Close</label>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {deal.expected_close_date 
                    ? new Date(deal.expected_close_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Not set'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Change Stage */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Change Stage</h3>
            <div className="flex flex-wrap gap-2">
              {stages.map(stage => (
                <button
                  key={stage.id}
                  onClick={() => setEditedDeal({ ...editedDeal, status: stage.id })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    editedDeal.status === stage.id 
                      ? 'border-transparent text-white shadow-sm' 
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
                  }`}
                  style={editedDeal.status === stage.id ? { backgroundColor: stage.color } : {}}
                >
                  {stage.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          {deal.remarks && (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Notes</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">{deal.remarks}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
          <Link to={`/leads/${deal.id}`} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            View Full Details →
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

// Main Component
function PipelinePage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [dealSizeFilter, setDealSizeFilter] = useState('all');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Get unique owners and industries for filters
  const owners = useMemo(() => {
    const uniqueOwners = [...new Set(leads.map(l => l.owner_name).filter(Boolean))];
    return uniqueOwners;
  }, [leads]);

  const industries = useMemo(() => {
    const uniqueIndustries = [...new Set(leads.map(l => l.industry).filter(Boolean))];
    return uniqueIndustries;
  }, [leads]);

  // Filter leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          lead.name?.toLowerCase().includes(query) ||
          lead.organization?.toLowerCase().includes(query) ||
          lead.email?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (ownerFilter !== 'all' && lead.owner_name !== ownerFilter) return false;
      if (industryFilter !== 'all' && lead.industry !== industryFilter) return false;
      if (dealSizeFilter !== 'all') {
        const value = lead.deal_value || 0;
        if (dealSizeFilter === 'small' && value >= 100000) return false;
        if (dealSizeFilter === 'medium' && (value < 100000 || value >= 500000)) return false;
        if (dealSizeFilter === 'large' && value < 500000) return false;
      }
      return true;
    });
  }, [leads, searchQuery, ownerFilter, industryFilter, dealSizeFilter]);

  // Group leads by stage
  const leadsByStage = useMemo(() => {
    const grouped = {};
    PIPELINE_STAGES.forEach(stage => {
      grouped[stage.id] = filteredLeads.filter(lead => lead.status === stage.id);
    });
    return grouped;
  }, [filteredLeads]);

  // KPI calculations
  const totalPipelineValue = filteredLeads.reduce((sum, lead) => sum + (lead.deal_value || 0), 0);
  const weightedPipeline = filteredLeads.reduce((sum, lead) => {
    const prob = lead.win_probability || 50;
    return sum + ((lead.deal_value || 0) * prob / 100);
  }, 0);
  const closedWonValue = filteredLeads
    .filter(l => l.status === 'closed_won')
    .reduce((sum, l) => sum + (l.deal_value || 0), 0);
  const closedDeals = filteredLeads.filter(l => ['closed_won', 'closed_lost'].includes(l.status));
  const winRate = closedDeals.length > 0 
    ? Math.round((filteredLeads.filter(l => l.status === 'closed_won').length / closedDeals.length) * 100)
    : 0;

  // Drag handlers
  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragOver = (event) => setOverId(event.over?.id || null);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeLead = leads.find(l => l.id === active.id);
    if (!activeLead) return;

    let targetStage = PIPELINE_STAGES.find(s => s.id === over.id)?.id;
    if (!targetStage) {
      const overLead = leads.find(l => l.id === over.id);
      if (overLead) targetStage = overLead.status;
    }

    if (targetStage && targetStage !== activeLead.status) {
      setLeads(prev => prev.map(l => 
        l.id === active.id ? { ...l, status: targetStage } : l
      ));

      try {
        await axios.put(`${API}/leads/${active.id}`, {
          ...activeLead,
          status: targetStage,
        });
      } catch (error) {
        console.error('Error updating lead status:', error);
        fetchLeads();
      }
    }
  };

  const handleDealClick = (deal) => {
    setSelectedDeal(deal);
    setDrawerOpen(true);
  };

  const handleDealUpdate = async (updatedDeal) => {
    try {
      await axios.put(`${API}/leads/${updatedDeal.id}`, updatedDeal);
      setLeads(prev => prev.map(l => l.id === updatedDeal.id ? updatedDeal : l));
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  };

  const activeDeal = activeId ? leads.find(l => l.id === activeId) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="pipeline-loading">
        <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6" data-testid="pipeline-page">
      {/* Header Section */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 lg:p-6 space-y-4">
        {/* Title Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Pipeline</h1>
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <Link to="/add-lead" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors" data-testid="add-deal-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Deal
          </Link>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPICard title="Total Pipeline" value={formatCurrency(totalPipelineValue)} trend={0} subValue={`${filteredLeads.length} deals`} />
          <KPICard title="Weighted Pipeline" value={formatCurrency(weightedPipeline)} trend={0} subValue="by probability" />
          <KPICard title="Closed Won" value={formatCurrency(closedWonValue)} trend={0} subValue={`${leadsByStage['closed_won']?.length || 0} deals`} />
          <KPICard title="Win Rate" value={`${winRate}%`} trend={0} subValue="of closed deals" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search deals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800"
              data-testid="search-deals"
            />
          </div>
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="filter-owner"
          >
            <option value="all">All Owners</option>
            {owners.map(owner => (
              <option key={owner} value={owner}>{owner}</option>
            ))}
          </select>
          <select
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="filter-industry"
          >
            <option value="all">All Industries</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
          <select
            value={dealSizeFilter}
            onChange={(e) => setDealSizeFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="filter-deal-size"
          >
            <option value="all">All Sizes</option>
            <option value="small">&lt; ₹1L</option>
            <option value="medium">₹1L - ₹5L</option>
            <option value="large">&gt; ₹5L</option>
          </select>
          {(searchQuery || ownerFilter !== 'all' || industryFilter !== 'all' || dealSizeFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setOwnerFilter('all');
                setIndustryFilter('all');
                setDealSizeFilter('all');
              }}
              className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-4 lg:p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 h-full min-w-max">
            {PIPELINE_STAGES.map(stage => (
              <PipelineColumn
                key={stage.id}
                stage={stage}
                deals={leadsByStage[stage.id] || []}
                onDealClick={handleDealClick}
                isOver={overId === stage.id}
              />
            ))}
          </div>
          <DragOverlay>
            {activeDeal && (
              <div className="rotate-3">
                <DealCard deal={activeDeal} isDragging={true} onClick={() => {}} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Drawer */}
      <DealDrawer
        deal={selectedDeal}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedDeal(null);
        }}
        onUpdate={handleDealUpdate}
        stages={PIPELINE_STAGES}
      />

      <style>{`
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}

export default PipelinePage;
